<template>
    <kiosk-team-details :teamId="props.teamId">
        <div class="col-span-12 lg:col-span-10 flex flex-col w-full max-w-(--breakpoint-sm) justify-center">
            <page-callout section="kiosk-team" class="mb-4" />

            <page-header>Details</page-header>

            <vx-expandable-card class="mb-6">
                <template v-slot:content>
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-col gap-1">
                            <strong>Name:</strong>
                            <div class="text-sm text-slate-500 flex items-center gap-1">
                                <vx-badge size="xs" color="slate">{{ team.id }}</vx-badge> {{ team.name }}
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Status:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-popover hover arrow placement="top">
                                    {{ formatTeamStatus(team.status) }}
                                    <template v-if="team.status === 'suspended'" v-slot:content>
                                        <div class="max-w-xs">
                                            {{ team.status_reason }}
                                        </div>
                                    </template>
                                </vx-popover>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Owner:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="ownerLoading"></vx-icon-loading>
                                <div v-else-if="owner" class="flex items-center gap-2">
                                    <div>{{ owner.name }}</div>
                                    <vx-button
                                        color="muted"
                                        size="xs"
                                        data-test="go-to-owner"
                                        :href="`/kiosk/users/${owner.id}`"
                                    >
                                        Go to Owner
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                        <div class="flex flex-col gap-1">
                            <strong>Users:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="usersCountLoading"></vx-icon-loading>
                                <div v-else-if="usersCount != undefined" class="flex items-center gap-2">
                                    {{ usersCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Pending Invites:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="invitationsCountLoading"></vx-icon-loading>
                                <div v-else-if="invitationsCount != undefined" class="flex items-center gap-2">
                                    {{ invitationsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Organizational Units:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="orgUnitsCountLoading"></vx-icon-loading>
                                <div v-else-if="orgUnitsCount != undefined" class="flex items-center gap-2">
                                    {{ orgUnitsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Groups:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="groupsCountLoading"></vx-icon-loading>
                                <div v-else-if="groupsCount != undefined" class="flex items-center gap-2">
                                    {{ groupsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Parent:</strong>
                            <div class="text-sm text-slate-500 flex items-center gap-1">
                                <vx-icon-loading spin v-if="parentLoading"></vx-icon-loading>
                                <template v-else-if="parent">
                                    <vx-badge size="xs" color="slate">{{ parent.id }}</vx-badge>
                                    <span>{{ parent.name }}</span>
                                    <vx-button
                                        class="ml-1"
                                        size="xs"
                                        color="muted"
                                        data-test="go-to-parent"
                                        :href="`/kiosk/teams/${parent.id}`"
                                    >
                                        Go to Parent
                                        <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                    </vx-button>
                                </template>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Sub Teams:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="teamsCountLoading"></vx-icon-loading>
                                <div v-else-if="teamsCount != undefined" class="flex items-center gap-2">
                                    {{ teamsCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Team Phone Numbers:</strong>
                            <div class="text-sm text-slate-500">
                                <vx-icon-loading spin v-if="phoneNumbersCountLoading"></vx-icon-loading>
                                <div v-else-if="phoneNumbersCount != undefined" class="flex items-center gap-2">
                                    {{ phoneNumbersCount }}
                                </div>
                                <template v-else> - </template>
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>

            <vx-expandable-card class="mb-6">
                <template v-slot:content>
                    <div class="text-xl font-extrabold mb-4">Resources</div>
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-col gap-1">
                            <strong>Phone Numbers:</strong>
                            <div class="text-sm text-slate-500 flex flex-col">
                                <vx-icon-loading spin v-if="phoneNumbersLoading"></vx-icon-loading>
                                <div v-else-if="!phoneNumbers.length">No phone numbers yet.</div>
                                <div v-for="phoneNumber in phoneNumbers" :key="phoneNumber.id">
                                    {{ formatPhoneNumber(phoneNumber.phone_number) }}
                                    {{ countryEmoji(phoneNumber.iso_country) }} &rarr;
                                    {{ formatNumberType(phoneNumber.number_type) }}
                                </div>
                            </div>
                            <div>
                                <vx-button
                                    color="muted"
                                    size="xs"
                                    data-test="phone-numbers-link"
                                    :href="`/kiosk/teams/${props.teamId}/phone-numbers`"
                                >
                                    See all
                                    <font-awesome-icon widthAuto :icon="faSquareArrowUpRight"></font-awesome-icon>
                                </vx-button>
                            </div>
                        </div>

                        <div class="flex flex-col gap-1">
                            <strong>Short URL:</strong>
                            <div class="text-sm text-slate-500 flex flex-col">
                                {{ team.domain || '-' }}
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>

            <vx-expandable-card>
                <template v-slot:content>
                    <div class="text-xl font-extrabold mb-4">Custom Attributes</div>
                    <vx-icon-loading spin v-if="customAttributesLoading"></vx-icon-loading>

                    <div v-else-if="!customAttributes.length" class="text-slate-500">No custom attributes yet.</div>

                    <div class="flex flex-col gap-4">
                        <div
                            v-for="customAttribute in customAttributes"
                            :key="customAttribute.key"
                            class="flex flex-col gap-1"
                        >
                            <strong>{{ customAttribute.key }}:</strong>
                            <div class="text-sm text-slate-500 break-all">
                                {{ customAttribute.value }}
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>
        </div>
    </kiosk-team-details>
</template>

<script setup>
import KioskTeamDetails from '~/components/kiosk/kiosk-teams/details/KioskTeamDetails.vue';
import { VxBadge, VxButton, VxExpandableCard, VxIconLoading, VxPopover } from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import PageCallout from '~/components/general/PageCallout.vue';
import { useTeamDetails } from '~/components/kiosk/kiosk-teams/composables/useTeamDetails';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faSquareArrowUpRight } from '@fortawesome/pro-solid-svg-icons';
import { formatPhoneNumber } from '~/components/filters';
import { countryEmoji } from '~/utils/string';
import { formatNumberType } from '~/constants/phoneNumbers';
import { formatTeamStatus } from '~/utils/team';

const {
    team,
    onMountedTeam,

    owner,
    ownerLoading,
    getOwner,

    parent,
    parentLoading,
    getParent,

    usersCount,
    usersCountLoading,
    getUsersCount,

    invitationsCount,
    invitationsCountLoading,
    getInvitationsCount,

    teamsCount,
    teamsCountLoading,
    getTeamsCount,

    orgUnitsCount,
    orgUnitsCountLoading,
    getOrgUnitsCount,

    groupsCount,
    groupsCountLoading,
    getGroupsCount,

    phoneNumbersCount,
    phoneNumbersCountLoading,
    getPhoneNumbersCount,

    phoneNumbers,
    phoneNumbersLoading,
    getPhoneNumbers,

    customAttributes,
    customAttributesLoading,
    getCustomAttributes,
} = useTeamDetails();

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
});

onMountedTeam(() => {
    getOwner();
    getParent();
    getUsersCount();
    getInvitationsCount();

    if (!team.value.parent_id) {
        getTeamsCount();
    }

    if (team.value.team_attributes?.contact_groups) {
        getOrgUnitsCount();
        getGroupsCount();
    }

    getPhoneNumbersCount();
    getPhoneNumbers({
        per_page: 5,
    });

    getCustomAttributes();
});
</script>
